import { AppError } from '@repo/error';
import { errorMessage, statusLabel, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/sheet';
import { FileTextIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { dateLabel } from '@/components/raven/shared';
import { trpc } from '@/providers/trpc';
import type { RouterOutputs } from '@/providers/trpc-client';

export const DocumentsSection = ({
  documents,
}: {
  documents: RouterOutputs['report']['getReport']['documents'];
}) => {
  useLocale();

  const [preview, setPreview] = useState<{ url: string; name: string; mimeType: string } | null>(
    null,
  );
  const download = trpc.report.getDocumentUrl.useMutation({
    onError: (cause) => toast.error(errorMessage(AppError.parse(cause))),
  });
  return (
    <div className="raven-panel">
      <h2>{t('reports.documents')}</h2>
      <p className="my-3 text-sm">
        {t('auth.viewUploadedDocumentsAndReviewTheirVerificationResults')}
      </p>
      <div className="overflow-x-auto">
        <table className="raven-table">
          <thead>
            <tr>
              <th>{t('auth.documentType')}</th>
              <th>{t('auth.uploadedOn')}</th>
              <th>{t('auth.verificationResult')}</th>
              <th>{t('auth.problems')}</th>
              <th>{t('reports.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {documents?.map((document) => (
              <tr key={document._id}>
                <td>
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="size-4 shrink-0" />
                    <span>
                      {statusLabel(document.documentType)}
                      <small className="raven-muted block">{document.fileName}</small>
                    </span>
                  </div>
                </td>
                <td>{dateLabel(document.uploadedAt, true)}</td>
                <td>
                  <span
                    className="raven-risk"
                    data-risk={
                      document.verification?.status === 'VERIFIED'
                        ? 'LOW'
                        : document.verification?.status === 'PROBLEMS_DETECTED'
                          ? 'HIGH'
                          : undefined
                    }
                  >
                    {document.verification?.status === 'VERIFIED'
                      ? t('auth.verifiedChecks')
                      : document.verification?.status === 'PROBLEMS_DETECTED'
                        ? t('auth.problemsDetected')
                        : t('auth.unassessed')}
                  </span>
                </td>
                <td>
                  <p className="max-w-xs text-xs">
                    {document.verification?.issues.map((issue) => issue.message).join('; ') ||
                      (document.verification?.status === 'VERIFIED'
                        ? t('auth.noProblemsDetectedByAvailableChecks')
                        : t('auth.verificationHasNotBeenCompleted'))}
                  </p>
                  {document.verification && (
                    <details className="mt-2 text-xs">
                      <summary>{t('auth.verificationDetails')}</summary>
                      {document.verification.issues.map((issue, index) => (
                        <p key={index}>
                          {issue.field}: {issue.evidence}
                        </p>
                      ))}
                      <p>{document.verification.limitations.join(' ')}</p>
                    </details>
                  )}
                </td>
                <td>
                  <Button
                    disabled={
                      !document.sourceReportId || !document.storageKey || download.isPending
                    }
                    onClick={() => {
                      if (document.storageKey && document.sourceReportId) {
                        download.mutate(
                          { reportId: document.sourceReportId, documentId: document._id },
                          {
                            onSuccess: ({ url }) => {
                              if (url) {
                                setPreview({
                                  url,
                                  name: document.fileName ?? t('extra.document'),
                                  mimeType: document.mimeType ?? '',
                                });
                              }
                            },
                          },
                        );
                      }
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    {t('auth.viewDocument')}{' '}
                  </Button>
                </td>
              </tr>
            ))}
            {!documents?.length && (
              <tr>
                <td colSpan={5}>{t('auth.noDocumentsUploaded')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Sheet
        onOpenChange={(open) => {
          if (!open) {
            setPreview(null);
          }
        }}
        open={Boolean(preview)}
      >
        <SheetContent className="w-full sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>{preview?.name}</SheetTitle>
            <SheetDescription>{t('auth.originalUploadedDocument')}</SheetDescription>
          </SheetHeader>
          {preview && (
            <>
              <a
                className="raven-link px-5"
                href={preview.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('auth.openDownloadOriginal')}{' '}
              </a>
              {preview.mimeType.startsWith('image/') ? (
                <img
                  alt={preview.name}
                  className="h-[75vh] w-full object-contain"
                  src={preview.url}
                />
              ) : (
                <iframe className="h-[75vh] w-full" src={preview.url} title={preview.name} />
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
